import React, { useCallback, useMemo, useState } from 'react';
import {
  View,
  FlatList,
  Text,
  TouchableOpacity,
  LayoutChangeEvent,
  ListRenderItemInfo,
} from 'react-native';
import { COUNTRIES, Country } from '@oolio-group/design-common';
import { getStyles } from './CountryModal.styles';
import { useTheme } from '../../../hooks/useTheme';
import { ButtonIcon } from '../../Button/ButtonIcon';
import Mask from '../../Mask/Mask';
import Flag from '../../Flag/Flag';

// Keep in sync with `option.width` and the row/column `gap` in CountryModal.styles.ts.
const OPTION_WIDTH = 200;
const OPTION_GAP = 8;

interface CountryModalProps {
  /** Code of the currently selected country, used to highlight the active option. */
  selectedCode?: Country['code'];
  /** Called with the chosen country when an option is pressed. */
  onSelect: (country: Country) => void;
  /** Called when the close button is pressed. */
  onClose: () => void;
}

const CountryModal: React.FC<CountryModalProps> = ({
  selectedCode,
  onSelect,
  onClose,
}) => {
  const { colors } = useTheme();
  const styles = useMemo(() => getStyles(colors), [colors]);
  const [numColumns, setNumColumns] = useState<number | null>(null);

  const handleLayout = useCallback((event: LayoutChangeEvent) => {
    const { width } = event.nativeEvent.layout;
    const columns = Math.max(
      1,
      Math.floor((width + OPTION_GAP) / (OPTION_WIDTH + OPTION_GAP)),
    );
    setNumColumns(columns);
  }, []);

  const renderItem = useCallback(
    ({ item: country }: ListRenderItemInfo<Country>) => (
      <TouchableOpacity
        onPress={() => onSelect(country)}
        style={[
          styles.option,
          country.code === selectedCode && styles.optionSelected,
        ]}
      >
        <Flag code={country.code} />
        <Text style={styles.optionText} numberOfLines={1}>
          {country.name}
        </Text>
      </TouchableOpacity>
    ),
    [onSelect, selectedCode, styles],
  );

  return (
    <View style={styles.container}>
      <ButtonIcon
        id="btn-close"
        icon="times"
        onPress={onClose}
        containerStyles={styles.btnClose}
      />
      <Text style={styles.title}>Select Country</Text>
      <View style={styles.listWrapper} onLayout={handleLayout}>
        {numColumns !== null && (
          <FlatList
            // FlatList requires remounting when numColumns changes.
            key={numColumns}
            style={styles.list}
            contentContainerStyle={styles.listContent}
            columnWrapperStyle={numColumns > 1 ? styles.listRow : undefined}
            data={COUNTRIES}
            numColumns={numColumns}
            keyExtractor={country => country.code}
            renderItem={renderItem}
          />
        )}
        <Mask
          height={48}
          color={colors.elevation1}
          containerStyle={styles.maskTop}
        />
        <Mask
          height={60}
          color={colors.elevation1}
          containerStyle={styles.maskBottom}
        />
      </View>
    </View>
  );
};

export default CountryModal;
